import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import VideosApiService from '../../services/videosApi';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { logger } from '../../lib/logger';

/**
 * Props pour le composant VideoPlayer
 */
interface VideoPlayerProps {
  /** ID Firestore de la vidéo (si fourni, charge l'entité Video) */
  videoId?: string;

  /** URL YouTube directe (si fourni, utilise directement sans chargement) */
  youtubeUrl?: string;

  /** URL directe vers une vidéo MP4 (Firebase Storage, etc.) */
  videoUrl?: string;

  /** Ratio d'aspect (défaut: "16/9") */
  aspectRatio?: string;

  /** Titre de la vidéo pour l'iframe */
  title?: string;

  /** Afficher un fond noir au lieu de gris (défaut: true si vidéo présente) */
  darkBackground?: boolean;
}

/**
 * Extrait l'ID YouTube depuis une URL YouTube
 *
 * Supporte les formats:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 */
const extractYouTubeId = (url: string): string | null => {
  if (!url) return null;

  // Format: https://www.youtube.com/watch?v=VIDEO_ID
  const match1 = url.match(/[?&]v=([^&]+)/);
  if (match1) return match1[1];

  // Format: https://youtu.be/VIDEO_ID
  const match2 = url.match(/youtu\.be\/([^?]+)/);
  if (match2) return match2[1];

  // Format: https://www.youtube.com/embed/VIDEO_ID
  const match3 = url.match(/youtube\.com\/embed\/([^?]+)/);
  if (match3) return match3[1];

  return null;
};

/**
 * Détecte si une URL est une vidéo directe (Firebase Storage, MP4, etc.)
 */
const isDirectVideoUrl = (url: string): boolean => {
  if (!url) return false;

  // Firebase Storage URLs
  if (url.includes('firebasestorage.googleapis.com')) return true;
  if (url.includes('storage.googleapis.com')) return true;

  // Direct video file extensions
  if (url.match(/\.(mp4|webm|ogg|mov)(\?|$)/i)) return true;

  return false;
};

/**
 * Composant VideoPlayer réutilisable
 *
 * Gère l'affichage d'une vidéo en acceptant soit:
 * - Un videoId (ID Firestore) → charge l'entité Video et extrait l'URL YouTube
 * - Une youtubeUrl directe → extrait l'ID YouTube et affiche l'iframe
 * - Une videoUrl directe → affiche avec le player HTML5 natif (Firebase Storage, MP4)
 *
 * Respecte les principes SOLID:
 * - SRP: Une seule responsabilité (afficher une vidéo)
 * - OCP: Extensible (supporte YouTube et vidéos directes)
 * - DIP: Dépend des abstractions (props), pas d'implémentation spécifique
 *
 * @example
 * // Avec ID Firestore
 * <VideoPlayer videoId="abc123" title="Ma vidéo" />
 *
 * // Avec URL YouTube directe
 * <VideoPlayer youtubeUrl="https://www.youtube.com/watch?v=dQw4w9WgXcQ" />
 *
 * // Avec URL Firebase Storage / MP4 directe
 * <VideoPlayer videoUrl="https://firebasestorage.googleapis.com/..." />
 */
const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoId,
  youtubeUrl,
  videoUrl,
  aspectRatio = '16/9',
  title = 'Vidéo',
  darkBackground,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [youtubeVideoId, setYoutubeVideoId] = useState<string | null>(null);
  const [directVideoUrl, setDirectVideoUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { environment } = useEnvironment();

  useEffect(() => {
    const loadVideo = async () => {
      // Reset states
      setYoutubeVideoId(null);
      setDirectVideoUrl(null);
      setError(null);

      // 1. Si videoUrl directe fournie (Firebase Storage, MP4)
      if (videoUrl) {
        if (isDirectVideoUrl(videoUrl)) {
          setDirectVideoUrl(videoUrl);
        } else {
          // Peut-être une URL YouTube passée par erreur en videoUrl
          const extractedId = extractYouTubeId(videoUrl);
          if (extractedId) {
            setYoutubeVideoId(extractedId);
          } else {
            setDirectVideoUrl(videoUrl); // Tenter comme vidéo directe
          }
        }
        return;
      }

      // 2. Si youtubeUrl fournie directement
      if (youtubeUrl) {
        // Vérifier si c'est en fait une URL Firebase Storage
        if (isDirectVideoUrl(youtubeUrl)) {
          setDirectVideoUrl(youtubeUrl);
        } else {
          const extractedId = extractYouTubeId(youtubeUrl);
          setYoutubeVideoId(extractedId);
        }
        return;
      }

      // 3. Si videoId Firestore fourni, charger l'entité Video
      if (videoId) {
        setLoading(true);

        try {
          const video = await VideosApiService.getVideoById(environment, videoId);

          if (video && video.youtubeUrl) {
            if (isDirectVideoUrl(video.youtubeUrl)) {
              setDirectVideoUrl(video.youtubeUrl);
            } else {
              const extractedId = extractYouTubeId(video.youtubeUrl);
              setYoutubeVideoId(extractedId);
            }
          } else {
            setError('URL vidéo non disponible');
          }
        } catch (err) {
          logger.error('[VideoPlayer] Erreur lors du chargement de la vidéo:', err);
          setError('Impossible de charger la vidéo');
        } finally {
          setLoading(false);
        }
      }
    };

    loadVideo();
  }, [videoId, youtubeUrl, videoUrl, environment]);

  const hasVideo = youtubeVideoId || directVideoUrl;

  // Déterminer le background
  const bgColor = darkBackground !== undefined
    ? (darkBackground ? '#000' : '#e0e0e0')
    : (hasVideo ? '#000' : '#e0e0e0');

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: bgColor,
        aspectRatio,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: hasVideo ? 0 : 3,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {loading ? (
        // État de chargement
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress size={40} sx={{ color: '#fff' }} />
          <Typography variant="body2" sx={{ color: '#999', fontSize: '14px' }}>
            Chargement de la vidéo...
          </Typography>
        </Box>
      ) : error ? (
        // État d'erreur
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Box
            sx={{
              width: '80px',
              height: '80px',
              bgcolor: '#d32f2f',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff' }}>
              ⚠
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              fontSize: '14px',
              textAlign: 'center',
            }}
          >
            {error}
          </Typography>
        </Box>
      ) : youtubeVideoId ? (
        // Afficher l'iframe YouTube
        <iframe
          src={`https://www.youtube.com/embed/${youtubeVideoId}?rel=0&modestbranding=1`}
          title={title}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : directVideoUrl ? (
        // Afficher le player HTML5 natif pour vidéos directes (Firebase Storage, MP4)
        <video
          ref={videoRef}
          src={directVideoUrl}
          title={title}
          controls
          controlsList="nodownload"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: '#000',
          }}
          onError={() => setError('Erreur lors du chargement de la vidéo')}
        >
          Votre navigateur ne supporte pas la lecture de vidéos.
        </video>
      ) : (
        // Placeholder (aucune vidéo)
        <>
          <Box
            sx={{
              width: '80px',
              height: '80px',
              bgcolor: '#9e9e9e',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '3px solid #757575',
            }}
          >
            <PlayArrow sx={{ fontSize: 40, color: '#fff' }} />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              fontSize: '14px',
              textAlign: 'center',
            }}
          >
            Aucune vidéo disponible
          </Typography>
        </>
      )}
    </Box>
  );
};

export default VideoPlayer;
